import React, {useEffect, useMemo, useRef, useState} from "react";
import {useNavigate} from "react-router-dom";
import RecommendationWhyBadge from "./RecommendationWhyBadge";


const GroupTiles = ({
  contextId,
  fetchRecommendationsForContext,
  recommendations = [],
  isLoading = false,
  viewInterceptor,
  devModeEnabled = false,
}) => {
  const fetchedFor = useRef(null);
  const navigate = useNavigate();
  const [selectedGroup, setSelectedGroup] = useState(null);

  useEffect(() => {
    if (!contextId) return;

    if (fetchedFor.current !== contextId) {
      fetchedFor.current = contextId;
      fetchRecommendationsForContext(contextId);
    }
  }, [contextId, fetchRecommendationsForContext]);

  const groupedStories = useMemo(() => {
    if (!Array.isArray(recommendations) || recommendations.length === 0) return [];

    const groups = new Map();

    recommendations.forEach((rec) => {
      // Group labels come back from the ranker in mixed shapes - some are
      // prefixed ("Category: Military"), some carry stray punctuation. Strip
      // that here so the section index reads like a masthead, not like data.
      const label =
        (rec.group ?? "")
          .trim()
          .replace(/^(category|topic|section)\s*[:\-\u2013]\s*/i, "")
          .replace(/[.,;:]+$/, "")
          .trim() || "Uncategorised";
      const metadata = rec.metadata;
      if (!metadata) return;

      if (!groups.has(label)) {
        groups.set(label, []);
      }

      const matchPercentage =
        typeof rec.score === "number"
          ? Math.round(rec.score * 100)
          : null;

      groups.get(label).push({
        uuid: rec.id || rec.entity_id,
        originalTitle: metadata.originalTitle || rec.name || "Untitled",
        aiTitle: metadata.originalTitle || rec.name || "Untitled",
        aiSummary: metadata.summary || rec.description || "",
        originalSummary: metadata.summary || rec.description || "",
        image: metadata.image || "",
        source: metadata.source || "",
        link: metadata.link || "",
        aiCategorization: metadata.aiCategorization || "",
        timeToRead: metadata.timeToRead || "",
        createdDate: metadata.createdDate || "",
        matchPercentage,
        recommendationSource: rec.recommendationSource || rec.source || "",
        recommendationExplanation:
          rec.recommendationExplanation || rec.explanation || rec._debug || null,
      });
    });

    return Array.from(groups.entries()).map(([label, stories]) => ({
      label,
      stories,
    }));
  }, [recommendations]);

  useEffect(() => {
    if (groupedStories.length === 0) {
      setSelectedGroup(null);
      return;
    }

    if (!selectedGroup) {
      setSelectedGroup(groupedStories[0].label);
      return;
    }

    const stillExists = groupedStories.some(
      (group) => group.label === selectedGroup
    );

    if (!stillExists) {
      setSelectedGroup(groupedStories[0].label);
    }
  }, [groupedStories, selectedGroup]);

  const activeGroup = useMemo(() => {
    if (!selectedGroup) return null;
    return groupedStories.find((group) => group.label === selectedGroup) || null;
  }, [groupedStories, selectedGroup]);

  const handleStoryNavigate = (story) => {
    if (!story?.uuid) return;
    try {
      sessionStorage.setItem(`neuron:article:${story.uuid}`, JSON.stringify(story));
    } catch { /* ignore */ }
    navigate(`/article/${story.uuid}`);
  };

  if (isLoading) {
    return (
      <div className="flex flex-wrap gap-x-6 gap-y-2 border-y border-rule py-3">
        {[...Array(6)].map((_, i) => (
          <div
            key={i}
            className="h-4 w-24 animate-pulse rounded-sm bg-surface-2"
          />
        ))}
      </div>
    );
  }

  if (!isLoading && groupedStories.length === 0) {
    return (
      <p className="py-8 text-center text-body text-ink-tertiary">
        No categories available for this user yet.
      </p>
    );
  }

  return (
    <div className="space-y-6">
      {/* Section index. A newspaper lists its sections in type, with a rule
          above and below - it does not sell them with photographs. */}
      <nav
        aria-label="Sections ranked for you"
        className="flex flex-wrap items-baseline gap-x-6 gap-y-1.5 border-y border-rule py-3"
      >
        {groupedStories.map((group) => {
          const isActive = selectedGroup === group.label;
          return (
            <button
              key={group.label}
              type="button"
              onClick={() => setSelectedGroup(group.label)}
              aria-current={isActive ? "true" : undefined}
              className={`group inline-flex items-baseline gap-1.5 border-b-2 pb-0.5 transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                isActive
                  ? "border-masthead"
                  : "border-transparent hover:border-rule-strong"
              }`}
            >
              <span
                className={`font-serif text-h3 font-bold leading-none ${
                  isActive ? "text-masthead" : "text-ink"
                }`}
              >
                {group.label}
              </span>
              <span className="font-sans text-kicker tabular-nums text-ink-muted">
                {group.stories.length}
              </span>
            </button>
          );
        })}
      </nav>

      <div className="border-t-2 border-ink pt-5">
        {activeGroup ? (
          <>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <p className="font-sans text-kicker uppercase tracking-[0.09em] text-masthead">
                  Selected category
                </p>
                <h4 className="font-serif text-h2 font-bold text-ink">
                  {activeGroup.label}
                </h4>
                <p className="font-sans text-meta text-ink-tertiary">
                  {activeGroup.stories.length} curated stor
                  {activeGroup.stories.length === 1 ? "y" : "ies"} just for you
                </p>
              </div>
            </div>

            <div className="mt-4 space-y-4">
              {activeGroup.stories.map((story) => (
                <article
                  key={story.uuid}
                  ref={viewInterceptor?.getItemRef(story.uuid)}
                  role="link"
                  tabIndex={0}
                  onClick={() => handleStoryNavigate(story)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      handleStoryNavigate(story);
                    }
                  }}
                  className="group relative flex cursor-pointer flex-col gap-5 border-b border-rule py-5 last:border-b-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-4 focus-visible:ring-offset-paper sm:flex-row"
                >
                  <div className="photo-inset relative h-40 w-full shrink-0 overflow-hidden rounded-sm bg-surface-2 sm:h-32 sm:w-48">
                    <img
                      src={story.image}
                      alt={story.aiTitle || story.originalTitle}
                      className="h-full w-full object-cover"
                    />
                    
                    {typeof story.matchPercentage === "number" && (
                      <span className="absolute bottom-2 left-2 rounded-sm bg-paper/92 px-2 py-0.5 font-sans text-kicker font-semibold text-pencil">
                        Match {story.matchPercentage}%
                      </span>
                    )}
                    <RecommendationWhyBadge
                      explanation={story.recommendationExplanation}
                      source={story.recommendationSource}
                      className="absolute top-2 right-2 z-20"
                      align="right"
                    />
                  </div>

                  <div className="flex flex-1 flex-col justify-between">
                    <div>
                      <p className="font-sans text-kicker font-semibold uppercase tracking-[0.09em] text-masthead">
                        {story.aiCategorization || "General"}
                      </p>
                      <h5 className="mt-1 font-serif text-h3 font-bold leading-tight text-ink decoration-masthead/40 underline-offset-[3px] group-hover:underline">
                        {story.aiTitle || story.originalTitle}
                      </h5>
                      <p className="mt-2 text-body text-ink-secondary line-clamp-3">
                        {story.aiSummary || story.originalSummary}
                      </p>
                    </div>
                    <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
                      <p className="font-sans text-meta text-ink-muted">
                        {story.timeToRead}
                      </p>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </>
        ) : (
          <div className="flex items-center justify-center py-12 text-body text-ink-tertiary">
            {"Select a category to preview its stories."}
          </div>
        )}
      </div>
    </div>
  );
};

export default GroupTiles;
